import type * as React from "react"

import { isHeldForSigning } from "../../Esign/helpers/signable-record.ts"

const CONTRACT_STATUSES = {
  DRAFT: "draft",
  PENDING_TO_REVIEW: "pending_review",
  APPROVED: "approved",
  DECLINED: "declined",
  CANCELLED: "cancelled",
  SENT_TO_CLIENT: "sent",
  SIGNED: "signed",
  // Retired: nothing sets it any more, but a contract that holds it still renders.
  COMPLETED: "completed"
} as const

type ContractStatusesKeys = keyof typeof CONTRACT_STATUSES
type ContractStatuses = (typeof CONTRACT_STATUSES)[ContractStatusesKeys]

const CONTRACT_STATUSES_OBJ: Record<
  string,
  {
    id: ContractStatuses
    value: ContractStatuses
    label: string
    style: React.CSSProperties
  }
> = {
  [CONTRACT_STATUSES.DRAFT]: {
    id: CONTRACT_STATUSES.DRAFT,
    value: CONTRACT_STATUSES.DRAFT,
    label: "Draft",
    style: {
      color: "#616d73",
      backgroundColor: "#ECEFF1",
      borderColor: "#ECEFF1"
    }
  },
  [CONTRACT_STATUSES.PENDING_TO_REVIEW]: {
    id: CONTRACT_STATUSES.PENDING_TO_REVIEW,
    value: CONTRACT_STATUSES.PENDING_TO_REVIEW,
    label: "In Review",
    style: {
      color: "#6e21f3",
      backgroundColor: "#efe3fd",
      borderColor: "#efe3fd"
    }
  },
  [CONTRACT_STATUSES.APPROVED]: {
    id: CONTRACT_STATUSES.APPROVED,
    value: CONTRACT_STATUSES.APPROVED,
    label: "Approved",
    style: {
      color: "#367B38",
      backgroundColor: "#E8F5E9",
      borderColor: "#E8F5E9"
    }
  },
  [CONTRACT_STATUSES.DECLINED]: {
    id: CONTRACT_STATUSES.DECLINED,
    value: CONTRACT_STATUSES.DECLINED,
    label: "Under Revision",
    style: {
      color: "#9E5E00",
      backgroundColor: "#FFF3E0",
      borderColor: "#FFF3E0"
    }
  },
  [CONTRACT_STATUSES.CANCELLED]: {
    id: CONTRACT_STATUSES.CANCELLED,
    value: CONTRACT_STATUSES.CANCELLED,
    label: "Cancelled",
    style: {
      color: "#D31F1B",
      backgroundColor: "#FFEBEE",
      borderColor: "#FFEBEE"
    }
  },
  [CONTRACT_STATUSES.SENT_TO_CLIENT]: {
    id: CONTRACT_STATUSES.SENT_TO_CLIENT,
    value: CONTRACT_STATUSES.SENT_TO_CLIENT,
    label: "Sent to Client",
    style: {
      color: "#007986",
      backgroundColor: "#E0F7FA",
      borderColor: "#E0F7FA"
    }
  },
  [CONTRACT_STATUSES.SIGNED]: {
    id: CONTRACT_STATUSES.SIGNED,
    value: CONTRACT_STATUSES.SIGNED,
    label: "Signed",
    style: {
      color: "#296E2C",
      backgroundColor: "#C8E6C9",
      borderColor: "#C8E6C9"
    }
  },
  [CONTRACT_STATUSES.COMPLETED]: {
    id: CONTRACT_STATUSES.COMPLETED,
    value: CONTRACT_STATUSES.COMPLETED,
    label: "Completed",
    style: {
      color: "#6F6F6F",
      backgroundColor: "#F5F5F5",
      borderColor: "#F5F5F5"
    }
  }
}

const CONTRACT_STATUSES_MAP = Object.fromEntries(
  Object.values(CONTRACT_STATUSES_OBJ).map((item) => [item.label, item])
) as Record<
  string,
  (typeof CONTRACT_STATUSES_OBJ)[keyof typeof CONTRACT_STATUSES_OBJ]
>

// The contract statuses the backend lets a contract be emailed from — `is_client_deliverable`.
const CONTRACT_SHARE_EMAIL_STATUSES: ContractStatuses[] = [
  CONTRACT_STATUSES.APPROVED,
  CONTRACT_STATUSES.SENT_TO_CLIENT
]

/** The server refuses to email a contract while a signature request holds it. */
const canShareContractByEmail = (contract: {
  status?: string
  in_signing?: boolean
}) =>
  CONTRACT_SHARE_EMAIL_STATUSES.includes(contract.status as ContractStatuses) &&
  !isHeldForSigning(contract)

export {
  CONTRACT_STATUSES,
  CONTRACT_SHARE_EMAIL_STATUSES,
  canShareContractByEmail,
  CONTRACT_STATUSES_OBJ,
  CONTRACT_STATUSES_MAP,
  type ContractStatuses
}
